// Derived column calculation engine
import { getCategoryConfig, getCategories } from '../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../data/yawehsData'
import { getProducts } from '../data/products'
import { getProductPrice } from './productPricing'

function getCategoriesForDept(dept) {
  if (dept === 'yawehs') return YAWEHS_CATEGORIES_SEED
  if (dept === 'bakery') return BAKERY_CATEGORIES_SEED
  return getCategories()
}

// Calculate projected sales for a category.
// Projected = opening stock value as entered in Morning Log.
// Surplus is NOT deducted here — it is applied at reconciliation time.
export function calculateProjectedSales(category, opening, surplus) {
  const config = getCategoryConfig(category)
  if (!config || !config.projectable) return null
  if (opening === null || opening === undefined) return null

  if (config.type === 'fixed_unit') {
    return opening * config.unitPrice
  }

  if (config.type === 'continuous_variable') {
    // Opening is in rands — projected = opening exactly
    return opening
  }

  if (config.type === 'discrete') {
    // Opening is piece count — projected = pieces × base piece cost
    const avgPrice = getAveragePriceForCategory(category)
    return opening * avgPrice
  }

  return null
}

// Get base piece cost for discrete categories.
// Uses effective cost per piece = unitPrice ÷ qps so that bundle products
// like KHW ("R12 for 3 pieces") give R4/piece instead of R12/piece.
// Returns the cheapest effective piece cost (the base unit).
export function getAveragePriceForCategory(category, department) {
  const priceOf = (p) => {
    const px = getProductPrice(p, department)
    return px == null ? 0 : Number(px) || 0
  }
  const products = getProducts(department).filter(
    p => p.category === category && priceOf(p) > 0 && p.saleType === 'discrete'
  )
  if (products.length === 0) return 0
  const effectiveCosts = products.map(p => priceOf(p) / (Number(p.qps) || 1))
  return Math.min(...effectiveCosts)
}

// Calculate variance
export function calculateVariance(actual, projected) {
  if (projected === null || projected === undefined) return null
  return actual - projected
}

// Calculate variance percentage
export function calculateVariancePct(actual, projected) {
  if (!projected || projected === 0) return null
  return ((actual - projected) / projected) * 100
}

// Calculate waste percentage
export function calculateWastePct(opening, surplus) {
  if (!opening || opening === 0) return null
  return (surplus / opening) * 100
}

// Calculate expected sales for a transaction
export function calculateExpectedSales(unitPrice, qty) {
  if (!unitPrice || !qty) return null
  return unitPrice * qty
}

// Build daily category summary.
// projected  = opening stock value from Morning Log
// surplus    = end-of-day leftover from surplus entry
// consumed   = actualSales from POS (what was actually sold)
// difference = projected − consumed − surplus
//   positive (> R2)  = SHORT (stock unaccounted for)
//   negative (< -R2) = OVER (sold more than stocked)
//   within ±R2       = RECONCILED (rounding tolerance)
export function buildDailyCategorySummary(category, opening, surplus, actualSales) {
  const projected = calculateProjectedSales(category, opening, surplus)
  const surplusVal = surplus || 0
  const consumed = actualSales || 0
  const difference = projected !== null ? projected - consumed - surplusVal : null
  const diffPct = projected && projected > 0 ? (difference / projected) * 100 : null
  const restockImplied = opening === 0 && actualSales > 0

  const TOLERANCE = 2
  let reconStatus = null
  // Only flag SHORT when projected > 0 (morning log was entered with a real value).
  // Projected = 0 or null means no data — not a shortfall.
  if (difference !== null && projected > 0) {
    if (difference > TOLERANCE) reconStatus = 'short'
    else reconStatus = 'reconciled'
  }

  return {
    category,
    openingValue: opening || 0,
    surplusValue: surplusVal,
    projectedSales: projected,
    consumed,
    actualSales: consumed,
    variance: difference,
    variancePct: diffPct,
    reconStatus,
    restockImplied,
  }
}

// Build full daily summary
export function buildDailySummary(date, salesTransactions, morningLogs, surplusEntries, flags, department) {
  const totalSales = salesTransactions.reduce((sum, t) => sum + (t.salesRands || 0), 0)
  const totalQty = salesTransactions.reduce((sum, t) => sum + (t.qtySold || 0), 0)
  const papEntry = morningLogs.find(m => m.category === 'pap only')
  const papQty = papEntry?.value || 0

  const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'long' })
  const d = new Date(date)
  const weekOfMonth = Math.ceil(d.getDate() / 7)

  const highFlags = flags.filter(f => f.severity === 'HIGH').length
  const medFlags = flags.filter(f => f.severity === 'MEDIUM').length
  const lowFlags = flags.filter(f => f.severity === 'LOW').length

  // Total projected and surplus
  let totalProjected = 0
  let totalSurplusValue = 0
  const deptCategories = department ? getCategoriesForDept(department) : getCategories()
  for (const cat of deptCategories) {
    const opening = morningLogs.find(m => m.category === cat.name)?.value || 0
    const surplus = surplusEntries.find(s => s.category === cat.name)?.value || 0
    const projected = calculateProjectedSales(cat.name, opening, surplus)
    if (projected !== null) totalProjected += projected
    if (cat.openingUnit === 'rands') totalSurplusValue += surplus
    else if (cat.type === 'fixed_unit') totalSurplusValue += surplus * cat.unitPrice
    else totalSurplusValue += surplus * getAveragePriceForCategory(cat.name, department)
  }

  const totalOpening = morningLogs.reduce((sum, m) => {
    const cfg = getCategoryConfig(m.category)
    if (!cfg) return sum
    if (cfg.openingUnit === 'rands') return sum + m.value
    if (cfg.type === 'fixed_unit') return sum + m.value * cfg.unitPrice
    return sum + m.value * getAveragePriceForCategory(m.category, department)
  }, 0)

  const wastePct = totalOpening > 0 ? (totalSurplusValue / totalOpening) * 100 : null

  return {
    date,
    dayOfWeek,
    weekOfMonth,
    totalSales,
    totalQty,
    papQty,
    flagCountHigh: highFlags,
    flagCountMedium: medFlags,
    flagCountLow: lowFlags,
    totalProjectedSales: totalProjected,
    totalSurplusValue,
    wastePct,
  }
}

// Statistics helpers
export function calcStats(values) {
  if (!values.length) return { total: 0, avg: 0, median: 0, stdDev: 0, min: 0, max: 0 }
  const sorted = [...values].sort((a, b) => a - b)
  const total = values.reduce((s, v) => s + v, 0)
  const avg = total / values.length
  const median = sorted.length % 2 === 0
    ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
    : sorted[Math.floor(sorted.length / 2)]
  const variance = values.reduce((s, v) => s + (v - avg) ** 2, 0) / values.length
  const stdDev = Math.sqrt(variance)
  return { total, avg, median, stdDev, min: sorted[0], max: sorted[sorted.length - 1] }
}

// Format currency
export function formatR(value) {
  if (value === null || value === undefined) return '—'
  return `R${Number(value).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

export function formatPct(value) {
  if (value === null || value === undefined) return '—'
  return `${Number(value).toFixed(1)}%`
}

export function formatDate(dateStr) {
  if (!dateStr) return '—'
  // Format as dd/mm/yyyy
  const d = new Date(dateStr)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

// Short format for charts: dd/mm
export function formatDateShort(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}
